/**
 * Commander - Instance-based command execution system with plugin registry and history
 * Each Commander instance maintains isolated plugins and state
 */
export const Commander = () => {
  const enabledPlugins = {}
  let getState = null

  /**
   * Register plugins in this Commander instance
   * @param {Object} pluginMap - Object of { pluginID: plugin, ... }
   */
  const plugins = (pluginMap) => {
    for (const [pluginID, plugin] of Object.entries(pluginMap)) {
      enabledPlugins[pluginID] = plugin
    }
  }

  /**
   * Define how to get current state for history tracking
   * @param {Function} getter - Function that returns current state
   */
  const setStateResolver = (getter) => {
    getState = getter
  }

  /**
   * Execute a command in the format "pluginID.action"
   * @param {string} command - Command string like "plugin.action"
   * @param {*} data - Data to pass to the plugin
   * @returns {*} Result from plugin execution
   */
  const runCommand = (command, data) => {
    const parts = command.split(".")
    const pluginID = parts[0]
    const action = parts.slice(1).join(".")

    let plugin = enabledPlugins[pluginID]
    if (!plugin) return

    // Handle plugin options
    let options
    if (Array.isArray(plugin)) {
      options = plugin.length > 1 && plugin[1]
      plugin = plugin[0]
    }

    // Check if action should skip history
    if (options?.useHistory && Array.isArray(options.useHistory)) {
      if (!options.useHistory.includes(action)) {
        return plugin({ action, data })
      }
    }

    // Run with history tracking
    if (!options || options.useHistory !== false) {
      const beforeState = getState?.()
      const result = plugin({ action, data })
      const afterState = getState?.()

      // Only add to history if state changed
      if (beforeState !== afterState && beforeState !== undefined && afterState !== undefined) {
        runCommand("history.add", beforeState)
        runCommand("history.add", afterState)
      }

      return result
    }

    // Run without history
    return plugin({ action, data })
  }

  return {
    plugins,
    setStateResolver,
    runCommand,
    getPlugins: () => ({ ...enabledPlugins }),
  }
}

/**
 * React hook for using Commander in components
 * Creates a stable Commander instance for the component lifecycle
 */
import { useMemo } from 'react'

export const useCommander = () => {
  return useMemo(() => Commander(), [])
}

/**
 * HTML Editor Commander - Shared instance for HTML Editor and its plugins
 * This is separate from per-component Commander instances
 */
export const HtmlEditorCommander = Commander()
